import { wenji } from "libraries/interfaces/IWenji";

import _has from 'lodash-es/has';
import _toUpper from 'lodash-es/toUpper';
import _toString from 'lodash-es/toString';
import * as moment from 'moment';

import { ChangeDetectionStrategy, Component, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { GenericFieldset, IGenericFieldset } from '../generic-fieldset.component';
import { FormBuilderService } from "libraries/service/formBuilder/formBuilder.service";
import { Date2monthsFieldComponent } from "../../fields/date2months-field/date2months-field.component";
import { WORDING } from "./config/wording";
import { ELabelPosition } from "libraries/module/formbuilder/fields/form-field/form-field.component";
import { IValidator } from "libraries/module/formbuilder/forms/utils/validators/IValidator";
import { Yii } from "libraries/interfaces/Yii";

/**
 * Segment-fieldset
 * Generate a fieldset of needed fields
 * @extends GenericFieldset
 * @implements IGenericFieldset
 */
@Component({
    selector: 'segment-fieldset',
    templateUrl: './segment-fieldset.component.html',
    styleUrls: ['./segment-fieldset.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class SegmentFieldsetComponent extends GenericFieldset implements IGenericFieldset {

    public wording= WORDING;
    /* Readonly context can't be modified. Define the context of the fieldset */
    public readonly context: string = 'segment';

    /* return today date **/
    public today = new Date();

    public labelPosition: ELabelPosition = ELabelPosition.right;

    public dateValidator: IValidator[] = [
        {
            validator: Validators.required,
            errors: {
                required: {
                    when: 'touched',
                    message: Yii.t('wui-shared-js', 'Date requise'),
                }
            }
        }
    ];

    @ViewChild('returnDate')
    public returnDate: Date2monthsFieldComponent;

    /*** LIFECYCLE ***/

    /** DI - Formbuilder */
    constructor(public fb: FormBuilder,
                public formBuilderService: FormBuilderService) {
        super(fb);
    }


    public ngOnInit() {
        if (this.config.context === 'air' && (!this.fieldsetData)) {
            this.setDirection();
            this.formBuilderService.setFirstOpening('searchAirTrip');
        } else if (this.config.context === 'air' && (this.fieldsetData)) {
            this.formBuilderService.setFirstOpening('searchAirTrip');
        }
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (_has(changes, ['config', 'currentValue'])) {
            this.setDirection();
        }
    }

    /*** METHODs ***/
    public setNumberFromBeginEndDate(index: number): void {
        const control: any = this.group;

        let begin = control.get([index, 'begin']);
        let end = control.get([index, 'end']);
        let night = control.get([index, 'duration', 'night']);

        let beginDate = moment(begin.value);
        const endDate = moment(end.value);

        if (begin.value && night.value && beginDate.diff(endDate, 'days') <= 0) {
            control.get([index, 'duration', 'night']).patchValue(-beginDate.diff(endDate, 'days'));
        } else if (begin.value) {
            night.patchValue('1');
            end.patchValue(beginDate.add(1, 'days').toDate());
        } else if (control.get([index, 'end']).value && night.value) {
            begin = control.get([index, 'begin']);
            night = control.get([index, 'duration', 'night']);
            end = control.get([index, 'end']);

            beginDate = moment(end.value).subtract(night.value, 'days');

            begin.patchValue(beginDate.toDate());
        }
    }

    public setEndDateFromNumber(index: number): any {
        let output: string | Date;

        if (this.group.get([index, 'begin']).value && this.group.get([index, 'duration', 'night']).value) {
            const begin = this.group.get([index, 'begin']).value;
            const night = this.group.get([index, 'duration', 'night']).value;

            output = moment(begin).add(night, 'days').toDate();
            // #TODO à changer pour plusieurs réservations
            this.group.get([index, 'end']).patchValue(output);
        } else {
            this.group.get([index, 'end']).patchValue('');
        }
    }

    /**
     * test if external search is Return
     */
    public isReturnSearch(): boolean {
        const control = this.group;

        return (control.length === 2 &&
            control.get(['0', 'to']).value === control.get(['1', 'from']).value &&
            control.get(['0', 'from']).value === control.get(['1', 'to']).value
        );
    }

    /**
     * seDirection
     * get Direction from config,
     * launched from ngOnInit
     */
    public setDirection(): void {
        switch (this.config.direction) {
            case 'Return':
                this.setReturn();
                break;
            case 'Oneway':
                this.firstField();
                break;
            default:
                break;
        }
    }


    /**
     * setReturn
     * set segment qtt to 2, 'Aller-retour'
     */
    public setReturn(): void {
        if (this.group.length === 1) {
            this.addFields();

            //TODO: EJ-2017-10-13: find a way to insure return airports
            const ctrl = this.group.get('0');
            this.setReturnFrom(ctrl, 'to');
            this.setReturnTo(ctrl, 'from');

        }

        if (this.group.length > 2) {
            this.firstField();
            this.addFields();
        }
        //TODO : if segment[0].from & .begin has value, set them to segment[1].begin and from
    }

    /**
     * setReturnFrom
     * patch Value "from" of second segment from first one, oin case of Return direction
     */
    public setReturnFrom(_ctrl: any, _path: any): void {
        const ctrl = _ctrl.get(_path);
        const value = ctrl.value;
        if (value) {
            const ctrl2 = this.group.get('1');
            ctrl2.patchValue({from: _toUpper(value)});
        }
    }

    /**
     * setReturnTo
     * patch Value "to" of second segment from first one, oin case of Return drection
     */
    public setReturnTo(_ctrl: any, _path: any): void {
        const ctrl = _ctrl.get(_path);
        const value = ctrl.value;

        if (value) {
            const ctrl2 = this.group.get('1');
            ctrl2.patchValue({to: _toUpper(value)});
        }
    }

    /**
     * getDate of index value
     */
    public getDate(_index: number): any {
        if (_index === 0) {
            return this.today;
        }
        const segment = this.group.get(_toString(_index));
        const dateField = segment.get('begin');
        if (dateField.value) {
            return dateField.value;
        }

    }

    /**
     * getLastDate, to ensure min Date in Multi-destination
     *
     */
    public getLastDate(_index: number): any {

        if (_index === 0) {
            return this.today;
        }

        const GROUP = this.group;
        for (let i = _index; i > 0; i--) {
            const segment = GROUP.get(_toString(i - 1));

            const dateField = segment.get('begin');

            if (dateField.value) {
                return dateField.value;
            }

        }

    }

    /**
     * getLastDate, to ensure min Date in Multi-destination
     *
     */
    public getNewDate(_index: number): any {
        if (_index === 0) {
            return this.today;
        } else {
            const lastDate = this.getLastDate(_index);

            //remove daysroundtrip LCBK => WUI
            return moment(lastDate)
                .add(wenji.locobook.search.daysroundtrip, 'days')
                .toDate();
        }
    }

    /**
     * setReturnDate, set return Date auto when blur of date1
     *
     */
    public setReturnDate(): any {
        const GROUP = this.group;
        let goDate = GROUP.get(['0', 'begin']);
        let backDate: any;
        if (goDate.value || goDate.value !== "") {
            backDate = this.getNewDate(1);
            if (!GROUP.get(['1', 'begin']).value) {
                GROUP.get(['1', 'begin']).patchValue(backDate);
            }
        }
    }


    public setFocus(_focusName: string, $event?: Event) {
        this[_focusName].openAndFocus();
    }

    public setDateAndNightFromBegin(index: number): void {
        const control: any = this.group;
        let night = control.get([index, 'duration', 'night']);

        if (control.get([index, 'begin']).value && !night.value) {
            const begin = control.get([index, 'begin']);
            const end = control.get([index, 'end']);
            const endDate: string | Date = moment(begin.value).add(this.config.night, 'days').toDate();

            night.patchValue(this.config.night);
            end.patchValue(endDate);
        } else if (control.get([index, 'begin']).value && night.value) {
            const begin = control.get([index, 'begin']);
            night = control.get([index, 'duration', 'night']);
            const end = control.get([index, 'end']);

            const endDate: string | Date = moment(begin.value).add(night.value, 'days').toDate();

            end.patchValue(endDate);
        }
    }

    public setNightFromEndOnChange(index: number): void {
        const control: any = this.group;
        const end = this.group.get([index, 'end']);

        if (end.value) {
            const night = control.get([index], 'duration', 'night');
            const begin = control.get([index], 'begin');

            if (begin.value) {
                night.patchValue(-begin.diff(end, 'days'));
            }
        }
    }




}
